import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Edit2, Bell, RotateCcw, ChevronUp, ChevronDown, Sparkles } from 'lucide-react';

/**
 * Extracted from TasksTab for single-responsibility & stable component identity.
 * All actions are received as props — this component is stateless.
 */
export const TaskList = ({
  tasks,
  type,
  title,
  description,
  color,
  onInitDMO,
  onReset,
  onOpenNewDialog,
  onMoveTask,
  onToggleComplete,
  onDeleteTask,
  onOpenEditDialog,
}) => {
  const isRecurring = type === 'recurring';

  return (
    <Card className={`border-${color}-200 dark:border-${color}-800`}>
      <CardHeader className="pb-2 flex flex-row items-start justify-between gap-3">
        <div className="min-w-0">
          <CardTitle className={`text-lg text-${color}-700 dark:text-${color}-300`}>
            {title}
          </CardTitle>
          {description && (
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed" data-testid={`tasklist-desc-${type}`}>
              {description}
            </p>
          )}
        </div>
        <div className="flex gap-2 shrink-0">
          {isRecurring && tasks.length === 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={onInitDMO}
              className="border-amber-300 text-amber-600 hover:bg-amber-50"
              data-testid="init-dmo-btn"
            >
              <Sparkles className="w-4 h-4 mr-1" />
              Add Suggestions
            </Button>
          )}
          {isRecurring && tasks.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={onReset}
              className={`border-${color}-300 text-${color}-600 hover:bg-${color}-50`}
              data-testid="reset-tasks-btn"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Reset
            </Button>
          )}
          <Button
            size="sm"
            className={`bg-${color}-600 hover:bg-${color}-700`}
            onClick={() => onOpenNewDialog(type)}
            data-testid={`add-task-btn-${type}`}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div>
          <AnimatePresence mode="popLayout">
            {tasks.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-muted-foreground mb-3">
                  No tasks yet. {isRecurring ? 'Add DMO suggestions or create your own!' : 'Add your first task!'}
                </p>
                {isRecurring && (
                  <Button
                    variant="outline"
                    onClick={onInitDMO}
                    className="border-amber-300 text-amber-600 hover:bg-amber-50"
                    data-testid="init-dmo-btn-empty"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Load DMO Suggestions
                  </Button>
                )}
              </div>
            ) : (
              <ul className="space-y-2">
                {tasks.map((task, index) => (
                  <motion.li
                    key={task.task_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ delay: index * 0.05 }}
                    layout
                    className={`flex items-start gap-2 p-3 rounded-xl transition-colors ${
                      task.completed
                        ? `bg-${color}-50/50 dark:bg-${color}-900/10`
                        : `bg-white dark:bg-slate-800 border border-${color}-100 dark:border-${color}-900`
                    }`}
                  >
                    <div className="flex flex-col gap-0.5">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => onMoveTask(task.task_id, 'up', isRecurring)}
                        disabled={index === 0}
                        className={`h-6 w-6 ${index === 0 ? 'opacity-30' : 'hover:bg-gray-100'}`}
                        data-testid={`move-up-${type}-${index}`}
                      >
                        <ChevronUp className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => onMoveTask(task.task_id, 'down', isRecurring)}
                        disabled={index === tasks.length - 1}
                        className={`h-6 w-6 ${index === tasks.length - 1 ? 'opacity-30' : 'hover:bg-gray-100'}`}
                        data-testid={`move-down-${type}-${index}`}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                    </div>

                    <Checkbox
                      checked={task.completed}
                      onCheckedChange={() => onToggleComplete(task, isRecurring)}
                      className={`mt-1 border-${color}-400 data-[state=checked]:bg-${color}-500 data-[state=checked]:border-${color}-500`}
                      data-testid={`task-checkbox-${type}-${index}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium ${task.completed ? 'line-through text-muted-foreground' : ''}`}>
                        {task.title}
                      </p>
                      {task.description && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                          {task.description}
                        </p>
                      )}
                      {task.reminder_date && (
                        <span className={`flex items-center gap-1 mt-2 text-xs text-${color}-600`}>
                          <Bell className="w-3 h-3" />
                          {new Date(task.reminder_date).toLocaleDateString()}
                          {task.reminder_time && ` ${task.reminder_time}`}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => onOpenEditDialog(task, type)}
                        className={`h-8 w-8 text-${color}-600 hover:bg-${color}-100`}
                        data-testid={`edit-task-${type}-${index}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => onDeleteTask(task.task_id, isRecurring)}
                        className="h-8 w-8 text-red-500 hover:bg-red-100"
                        data-testid={`delete-task-${type}-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </motion.li>
                ))}
              </ul>
            )}
          </AnimatePresence>
        </div>
      </CardContent>
    </Card>
  );
};

export default TaskList;
